<template>
  <div class="centered-container feedback-page">
    <h2 class="promo-heading">
      Wish PickyThing did something else? Request it, or +1 an existing idea.
      To report a bug or send a private note, use the buttons below — those stay
      between you and the maintainer. No account, no email required.
    </h2>

    <div class="action-row">
      <button type="button" @click="openForm('feature')">Request a feature</button>
      <button type="button" @click="openForm('bug')">Report a bug</button>
      <button type="button" @click="openForm('general')">Send feedback</button>
    </div>

    <p v-if="thanks" class="thanks">{{ thanks }}</p>
    <p v-if="error" class="error">{{ error }}</p>

    <div class="content-box">
      <div class="box-heading">
        <h3>Feature requests</h3>
      </div>
      <div class="box-content">
        <p v-if="loading" class="empty-note">Loading…</p>
        <p v-else-if="!openItems.length" class="empty-note">No open requests yet. Be the first.</p>
        <ul>
          <li v-for="item in openItems" :key="item.id" class="feature-item">
            <div class="item-top">
              <span class="item-title">{{ item.title }}</span>
              <button
                type="button"
                class="plus-button"
                :class="{ voted: isVoted(item.id) }"
                :disabled="voteBusy === item.id"
                @click="toggleVote(item)"
              >
                +1 {{ item.plusCount || 0 }}
              </button>
            </div>
            <p v-if="item.details" class="item-details">{{ item.details }}</p>
          </li>
        </ul>
      </div>
    </div>

    <div v-if="handledItems.length" class="content-box">
      <div class="box-heading">
        <h3>Recently handled</h3>
      </div>
      <div class="box-content">
        <ul>
          <li v-for="item in handledItems" :key="item.id" class="handled-item">
            <span class="item-title">{{ item.title }}</span>
            <span class="status-chip">{{ statusLabel(item.status) }}</span>
          </li>
        </ul>
      </div>
    </div>

    <ModalDialog
      :show="!!formType"
      :title="formTitle"
      :message="formHelper"
      :confirm-text="submitting ? 'Sending…' : 'Submit'"
      :confirm-disabled="!canSubmit"
      @confirm="submitForm"
      @cancel="closeForm"
    >
      <label class="field-label" for="feedback-title">Title</label>
      <input
        id="feedback-title"
        v-model="form.title"
        type="text"
        maxlength="120"
        class="form-input"
        @keyup.enter="submitForm"
      />
      <label class="field-label" for="feedback-details">Details (optional)</label>
      <textarea
        id="feedback-details"
        v-model="form.details"
        maxlength="2000"
        class="form-textarea"
        rows="4"
      ></textarea>
      <label class="field-label" for="feedback-contact">How can I reach you? (optional)</label>
      <input
        id="feedback-contact"
        v-model="form.contact"
        type="text"
        maxlength="200"
        class="form-input"
        placeholder="Email, if you want a reply"
      />
      <div class="hp" aria-hidden="true">
        <label for="feedback-company">Company</label>
        <input id="feedback-company" v-model="form.company" type="text" tabindex="-1" autocomplete="off" />
      </div>
    </ModalDialog>
  </div>
</template>

<script>
import ModalDialog from "@/components/Modal.vue";
import { feedbackRequest, getVotedIds, getVoterId, setVotedIds } from "@/utils/feedback";

const FORM_COPY = {
  feature: {
    title: "Request a feature",
    helper: "This will appear on the public board so others can +1 it."
  },
  bug: {
    title: "Report a bug",
    helper: "This stays private. Include the session URL if you have one, and what you expected vs what happened."
  },
  general: {
    title: "Send feedback",
    helper: "Anything that is not a bug or a feature request — praise, confusion, “this was useful,” etc. This stays private."
  }
};

export default {
  name: "FeedbackBoard",
  components: { ModalDialog },
  data() {
    return {
      loading: true,
      error: "",
      thanks: "",
      openItems: [],
      handledItems: [],
      votedIds: getVotedIds(),
      voteBusy: null,
      formType: null,
      submitting: false,
      form: {
        title: "",
        details: "",
        contact: "",
        company: ""
      }
    };
  },
  computed: {
    formTitle() {
      return this.formType ? FORM_COPY[this.formType].title : "";
    },
    formHelper() {
      return this.formType ? FORM_COPY[this.formType].helper : "";
    },
    canSubmit() {
      const title = this.form.title.trim();
      return !this.submitting && !!this.formType && title.length >= 3 && title.length <= 120;
    }
  },
  async mounted() {
    await this.loadItems();
  },
  methods: {
    statusLabel(status) {
      const labels = {
        implemented: "Implemented",
        rejected: "Rejected",
        duplicate: "Duplicate",
        resolved: "Resolved"
      };
      return labels[status] || status;
    },
    isVoted(id) {
      return this.votedIds.includes(id);
    },
    markVoted(id, voted) {
      const next = voted
        ? [...this.votedIds, id]
        : this.votedIds.filter((existing) => existing !== id);
      this.votedIds = [...new Set(next)];
      setVotedIds(this.votedIds);
    },
    async loadItems() {
      this.loading = true;
      this.error = "";
      try {
        const data = await feedbackRequest("GET", "feedback");
        this.openItems = data.open || [];
        this.handledItems = data.handled || [];
      } catch (err) {
        this.error = err.message || "Could not load feedback.";
      } finally {
        this.loading = false;
      }
    },
    openForm(type) {
      this.formType = type;
      this.thanks = "";
      this.error = "";
      this.form = { title: "", details: "", contact: "", company: "" };
    },
    closeForm() {
      this.formType = null;
      this.submitting = false;
    },
    async submitForm() {
      if (!this.canSubmit) return;
      this.submitting = true;
      this.error = "";
      try {
        const payload = {
          type: this.formType,
          title: this.form.title.trim(),
          details: this.form.details.trim(),
          contact: this.form.contact.trim(),
          company: this.form.company,
          voterId: getVoterId()
        };
        const result = await feedbackRequest("POST", "feedback", { body: payload });
        if (this.formType === "feature" && result && result.id) {
          this.openItems = [result, ...this.openItems.filter((item) => item.id !== result.id)];
          this.markVoted(result.id, true);
        } else {
          this.thanks = "Got it — this stays between you and the maintainer.";
        }
        this.closeForm();
      } catch (err) {
        this.error = err.message || "Could not submit.";
        this.submitting = false;
      }
    },
    async toggleVote(item) {
      if (this.voteBusy) return;
      this.voteBusy = item.id;
      this.error = "";
      try {
        const result = await feedbackRequest("POST", `feedback/${item.id}/vote`, {
          body: { voterId: getVoterId() }
        });
        item.plusCount = result.plusCount;
        this.markVoted(item.id, result.voted);
      } catch (err) {
        this.error = err.message || "Could not save your +1.";
      } finally {
        this.voteBusy = null;
      }
    }
  }
};
</script>

<style scoped>
.action-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-top: 16px;
}

.action-row button {
  margin-left: 0;
}

.thanks {
  margin-top: 12px;
  color: #1e8449;
  line-height: 1.4;
}

.empty-note {
  color: #666;
  line-height: 1.4;
}

.feature-item,
.handled-item {
  text-align: left;
  padding: 12px 0;
  border-bottom: 1px solid #eee;
}

.feature-item:last-child,
.handled-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.item-top,
.handled-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.item-title {
  font-weight: bold;
  line-height: 1.3;
}

.item-details {
  margin-top: 8px;
  color: #333;
  line-height: 1.4;
  white-space: pre-wrap;
}

.plus-button {
  margin-left: 0;
  flex-shrink: 0;
  padding: 6px 10px;
}

.plus-button.voted {
  background-color: #1e8449;
}

.status-chip {
  flex-shrink: 0;
  font-size: 12px;
  background-color: #eee;
  color: #333;
  border-radius: 3px;
  padding: 4px 8px;
  text-transform: capitalize;
}

.field-label {
  display: block;
  text-align: left;
  font-size: 13px;
  margin: 10px 0 6px;
  color: #333;
}

.form-input,
.form-textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 8px;
  border: 1px solid #ccc;
  font-family: inherit;
  font-size: 14px;
}

.form-textarea {
  min-height: 80px;
  resize: vertical;
}

.hp {
  position: absolute;
  left: -9999px;
  height: 0;
  overflow: hidden;
}

@media (max-width: 600px) {
  .action-row button,
  .plus-button {
    min-height: 44px;
  }

  .action-row button {
    flex: 1 1 100%;
  }
}
</style>
